import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, X, RefreshCw, Star, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const STATUS_TABS = [
  { v: "pending_review", label: "Pending Review", accent: true },
  { v: "all", label: "All Campaigns" },
];

const STATUSES = ["all", "pending_review", "live", "changes_requested", "rejected", "funded", "ended"];

const statusPill = (status) => {
  const map = {
    pending_review: "bg-amber-100 text-amber-800",
    live: "bg-emerald-100 text-emerald-800",
    changes_requested: "bg-sky-100 text-sky-800",
    rejected: "bg-red-100 text-red-800",
    funded: "bg-forest-500/15 text-forest-700",
    ended: "bg-slate-100 text-navy-700",
  };
  return map[status] || "bg-slate-100 text-navy-700";
};

const AdminCampaigns = () => {
  const { api, admin } = useAdminAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("status") === "pending_review" ? "pending_review" : (params.get("tab") || "pending_review");
  const [tab, setTab] = useState(initialTab);
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [categoryFilter, setCategoryFilter] = useState(params.get("category") || "all");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {kind: 'reject'|'request-changes', campaign}
  const [reason, setReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const isSuper = admin?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    const q = {};
    if (tab === "pending_review") q.status = "pending_review";
    else {
      if (statusFilter !== "all") q.status = statusFilter;
      if (categoryFilter !== "all") q.category = categoryFilter;
    }
    try {
      const { data } = await api.get("/admin/campaigns", { params: q });
      setCampaigns(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, statusFilter, categoryFilter]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (tab) p.set("tab", tab);
    if (tab === "all" && statusFilter !== "all") p.set("status", statusFilter);
    if (categoryFilter !== "all") p.set("category", categoryFilter);
    setParams(p, { replace: true });
    // eslint-disable-next-line
  }, [tab, statusFilter, categoryFilter]);

  const approve = async (c) => {
    if (!isSuper) return toast.error("Super admin access required");
    setActionBusy(true);
    try {
      await api.post(`/admin/campaigns/${c.id}/approve`);
      toast.success("Approved — campaign is now live");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setActionBusy(false); }
  };

  const submitReason = async () => {
    if (!modal || !reason.trim()) return toast.error("Please provide a reason");
    setActionBusy(true);
    try {
      const path = modal.kind === "reject" ? "reject" : "request-changes";
      await api.post(`/admin/campaigns/${modal.campaign.id}/${path}`, { reason });
      toast.success(modal.kind === "reject" ? "Campaign rejected" : "Changes requested");
      setModal(null); setReason(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setActionBusy(false); }
  };

  const toggleFeatured = async (c) => {
    setActionBusy(true);
    try {
      await api.post(`/admin/campaigns/${c.id}/featured`, { featured: !c.featured });
      toast.success(!c.featured ? "Added to Featured" : "Removed from Featured");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setActionBusy(false); }
  };

  const categories = useMemo(() => Array.from(new Set(campaigns.map((c) => c.category))).sort(), [campaigns]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-campaigns-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Moderation</span>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Campaigns</h1>
          <p className="mt-1 text-[#0D1B2A]/60 text-sm">Approve, reject or request changes. Featured campaigns appear on the public homepage.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-[#0D1B2A]/15 px-3 py-2 text-sm font-medium hover:bg-[#0D1B2A]/5">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2" data-testid="admin-campaigns-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            data-testid={`admin-tab-${t.v}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${tab === t.v
              ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
              : "bg-white text-[#0D1B2A]/80 border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "all" && (
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-filter-status"
            className="rounded-xl border border-[#0D1B2A]/15 bg-white px-3 py-2 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="admin-filter-category"
            className="rounded-xl border border-[#0D1B2A]/15 bg-white px-3 py-2 text-sm">
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c.toLowerCase().replace(" ", "-")}>{c}</option>)}
          </select>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center text-[#0D1B2A]/60"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center text-[#0D1B2A]/60" data-testid="admin-empty">
            {tab === "pending_review" ? "🎉 No campaigns awaiting review." : "No campaigns match your filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFFBF5]">
                <tr className="text-left text-xs uppercase tracking-widest text-[#0D1B2A]/50">
                  <th className="px-5 py-3">Campaign</th>
                  <th className="px-5 py-3">Creator</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Goal</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0D1B2A]/5">
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`admin-row-${c.id}`} className="hover:bg-[#FFFBF5]/60">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-[#0D1B2A]">{c.title}</div>
                      <div className="text-xs text-[#0D1B2A]/60 line-clamp-1 max-w-md">{c.tagline}</div>
                    </td>
                    <td className="px-5 py-4 text-[#0D1B2A]/80">{c.creator?.name || "—"}</td>
                    <td className="px-5 py-4 text-[#0D1B2A]/80">{c.category}</td>
                    <td className="px-5 py-4 font-medium">${(c.goal || 0).toLocaleString()}</td>
                    <td className="px-5 py-4 text-[#0D1B2A]/60 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusPill(c.status)}`}>
                        {c.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {c.status === "pending_review" && (
                          <>
                            <button
                              disabled={actionBusy || !isSuper}
                              onClick={() => approve(c)}
                              data-testid={`approve-${c.id}`}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2.5 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                              title={isSuper ? "Approve" : "Super admin required"}
                            >
                              <Check className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button
                              disabled={actionBusy || !isSuper}
                              onClick={() => { setModal({ kind: "request-changes", campaign: c }); setReason(""); }}
                              data-testid={`request-changes-${c.id}`}
                              className="inline-flex items-center gap-1 rounded-lg bg-sky-600 text-white px-2.5 py-1.5 text-xs font-semibold hover:bg-sky-700 disabled:opacity-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" /> Changes
                            </button>
                            <button
                              disabled={actionBusy || !isSuper}
                              onClick={() => { setModal({ kind: "reject", campaign: c }); setReason(""); }}
                              data-testid={`reject-${c.id}`}
                              className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-2.5 py-1.5 text-xs font-semibold hover:bg-red-700 disabled:opacity-50"
                            >
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                          </>
                        )}
                        {["live", "funded", "ended"].includes(c.status) && (
                          <button
                            disabled={actionBusy}
                            onClick={() => toggleFeatured(c)}
                            data-testid={`featured-toggle-${c.id}`}
                            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold border transition-colors ${c.featured
                              ? "bg-[#E85D04] text-white border-[#E85D04]"
                              : "bg-white text-[#0D1B2A]/80 border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5"}`}
                          >
                            <Star className={`h-3.5 w-3.5 ${c.featured ? "fill-current" : ""}`} /> {c.featured ? "Featured" : "Feature"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reason modal */}
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) { setModal(null); setReason(""); } }}>
        <DialogContent className="sm:max-w-[520px] rounded-2xl" data-testid="admin-reason-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0D1B2A]">
              {modal?.kind === "reject" ? "Reject campaign" : "Request changes"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#0D1B2A]/70">
            This will be sent to the creator{modal?.campaign?.creator?.name ? ` (${modal.campaign.creator.name})` : ""} — please write a helpful, specific note.
          </p>
          <Textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="admin-reason-input"
            rows={5} placeholder="e.g. Please add a clearer cover image and more details about your delivery timeline…"
            className="rounded-xl border-[#0D1B2A]/15 focus-visible:ring-[#E85D04]"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setModal(null); setReason(""); }} className="rounded-xl px-4 py-2 text-sm border border-[#0D1B2A]/15">Cancel</button>
            <button
              onClick={submitReason}
              disabled={actionBusy || !reason.trim()}
              data-testid="admin-reason-submit"
              className="rounded-xl bg-[#E85D04] hover:bg-[#c94d02] disabled:opacity-70 text-white px-4 py-2 text-sm font-semibold"
            >
              {actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (modal?.kind === "reject" ? "Reject campaign" : "Send request")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCampaigns;
